import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Flame,
  Snowflake,
  Sparkles,
  Wind,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SERVICES = [
  { id: "ac", label: "AC Repair & Install", icon: Snowflake, range: [180, 620] },
  { id: "heat", label: "Furnace & Heating", icon: Flame, range: [220, 780] },
  { id: "split", label: "Mini-Split Install", icon: Wind, range: [2900, 6800] },
  { id: "maint", label: "Maintenance Plan", icon: CalendarClock, range: [149, 349] },
  { id: "iaq", label: "Air Quality", icon: Sparkles, range: [450, 1450] },
];

const SIZES = ["Under 1,500 sq ft", "1,500 – 2,500 sq ft", "2,500 – 4,000 sq ft", "4,000+ sq ft"];
const MULT = [1, 1.15, 1.35, 1.6];

const round10 = (n) => Math.round(n / 10) * 10;

export default function QuoteModal({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [size, setSize] = useState(null);
  const [zip, setZip] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setStep(0);
    setService(null);
    setSize(null);
    setZip("");
  };

  const estimate =
    service !== null && size !== null
      ? SERVICES[service].range.map((v) => round10(v * MULT[size]))
      : null;

  const submit = async () => {
    if (!/^\d{5}$/.test(zip)) {
      toast.error("Enter a valid 5-digit zip code");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ service: SERVICES[service].id, size, zip }),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Estimate locked in", {
        description: `${SERVICES[service].label} · $${estimate[0].toLocaleString()}–$${estimate[1].toLocaleString()} · A Crowson comfort engineer will call ${zip} today.`,
      });
      onOpenChange(false);
      reset();
    } catch {
      toast.error("Couldn't send the request", {
        description: "Please call 615-593-2628 for your free quote.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        data-testid="quote-modal"
        className="border-white/10 bg-navy-900/95 text-slate-100 backdrop-blur-xl sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight text-slate-50">
            Instant Quote Estimator
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Three steps. Thirty seconds. Real numbers.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-2 mt-1 flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                i <= step ? "bg-forge" : "bg-white/10"
              }`}
            />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-2.5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
              Step 1 — What do you need?
            </p>
            <div data-testid="quote-service-select" className="grid gap-2">
              {SERVICES.map((s, i) => {
                const Icon = s.icon;
                const on = service === i;
                return (
                  <button
                    key={s.id}
                    data-testid={`quote-service-${s.id}`}
                    onClick={() => {
                      setService(i);
                      setStep(1);
                    }}
                    className={`flex items-center gap-3.5 rounded-xl border px-4 py-3.5 text-left text-sm font-semibold transition-colors duration-200 ${
                      on
                        ? "border-forge/50 bg-forge/10 text-slate-50"
                        : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25"
                    }`}
                  >
                    <Icon className={`h-4.5 w-4.5 h-5 w-5 ${on ? "text-forge" : "text-slate-500"}`} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-2.5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
              Step 2 — How big is your home?
            </p>
            <div data-testid="quote-home-size-input" className="grid grid-cols-2 gap-2">
              {SIZES.map((s, i) => (
                <button
                  key={s}
                  data-testid={`quote-size-${i}`}
                  onClick={() => {
                    setSize(i);
                    setStep(2);
                  }}
                  className={`rounded-xl border px-4 py-4 text-sm font-semibold transition-colors duration-200 ${
                    size === i
                      ? "border-forge/50 bg-forge/10 text-slate-50"
                      : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/25"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              data-testid="quote-back-step1"
              onClick={() => setStep(0)}
              className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          </div>
        )}

        {step === 2 && estimate && (
          <div className="space-y-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
              Step 3 — Your ballpark &amp; zip
            </p>
            <div className="rounded-xl border border-forge/30 bg-forge/[0.07] p-5">
              <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-slate-400">
                {SERVICES[service].label} · {SIZES[size]}
              </div>
              <div data-testid="quote-estimate" className="mt-2 font-display text-3xl font-extrabold text-slate-50">
                ${estimate[0].toLocaleString()} – ${estimate[1].toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-slate-400">
                Final quote confirmed on-site. Always free.
              </div>
            </div>
            <input
              data-testid="quote-zip-input"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder="Your zip code"
              inputMode="numeric"
              className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-forge/50 focus:outline-none"
            />
            <div className="flex items-center justify-between">
              <button
                data-testid="quote-back-step2"
                onClick={() => setStep(1)}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-200"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
              <button
                data-testid="quote-form-submit"
                onClick={submit}
                disabled={sending}
                className="flex items-center gap-2 rounded-full bg-gradient-to-br from-forge to-forge-hi px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-white shadow-[0_8px_28px_rgba(255,107,0,0.35)] transition-transform duration-200 hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? "Sending..." : "Lock It In"} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
